/**
 * Before / After expander for one audit row (TMMAPP-0022). Two columns on `md+`, stacked on
 * `xs`. Changed top-level keys use the existing brand wash (`tokens.brandSoft` = `selected`).
 */
import { Box, Grid, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AuditEvent } from '@/api/audit';
import { resolveTokens } from '@/theme/theme';
import {
  asRecord,
  changedKeys,
  formatNested,
  formatScalar,
  type FormatCtx,
} from './formatAuditValue';

interface AuditDetailsProps {
  event: AuditEvent;
  ctx: FormatCtx;
}

function ValueList({
  record,
  changed,
  ctx,
  wash,
}: {
  record: Record<string, unknown> | null;
  changed: ReadonlySet<string>;
  ctx: FormatCtx;
  wash: string;
}) {
  const { t } = useTranslation();
  if (record === null || Object.keys(record).length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('audit.noChange')}
      </Typography>
    );
  }
  const currency =
    typeof record.currency === 'string' && record.currency.length > 0
      ? record.currency
      : ctx.currency;
  return (
    <Stack spacing={0.5}>
      {Object.entries(record).map(([key, value]) => {
        const nested =
          typeof value === 'object' && value !== null
            ? formatNested(value, ctx, currency)
            : formatScalar(key, value, ctx, currency);
        return (
          <Box
            key={key}
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: changed.has(key) ? wash : undefined,
            }}
          >
            {/* On the wash only `text` is guaranteed (`text / brandSoft`), not `text2`. */}
            <Typography
              variant="caption"
              color={changed.has(key) ? 'text.primary' : 'text.secondary'}
              component="div"
            >
              {key}
            </Typography>
            <Typography variant="mono" component="div" sx={{ overflowWrap: 'anywhere' }}>
              {nested}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
}

export function AuditDetails({ event, ctx }: AuditDetailsProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const before = asRecord(event.before);
  const after = asRecord(event.after);
  const changed = changedKeys(before, after);
  const ids = [
    event.requestId.length > 0 ? t('audit.requestId', { id: event.requestId }) : null,
    event.aiRequestId !== null ? t('audit.aiRequestId', { id: event.aiRequestId }) : null,
  ].filter((bit): bit is string => bit !== null);

  return (
    <Box sx={{ py: 1 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('audit.before')}
          </Typography>
          <ValueList record={before} changed={changed} ctx={ctx} wash={tokens.brandSoft} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('audit.after')}
          </Typography>
          <ValueList record={after} changed={changed} ctx={ctx} wash={tokens.brandSoft} />
        </Grid>
      </Grid>
      {ids.length > 0 && (
        <Typography variant="mono" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {ids.join(' · ')}
        </Typography>
      )}
    </Box>
  );
}
